package app.footyguide.web.page

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import app.footyguide.web.data.FirestoreDatabase
import app.footyguide.web.data.LiveMatchScore
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlin.js.asDynamic
import kotlin.random.Random
import org.w3c.dom.events.Event

private const val LIVE_REFRESH_CHANNEL_PREFIX = "footyguide-live-refresh"
private const val LIVE_REFRESH_LEASE_PREFIX = "fg-live-refresh-lease-"

@Composable
fun LiveRefreshEffect(
    enabled: Boolean,
    leaseKey: String? = null,
    watchPath: String? = null,
    intervalMs: Long = DEFAULT_LIVE_REFRESH_INTERVAL_MS,
    onRefresh: suspend () -> Unit,
    onBroadcastRefresh: suspend (List<LiveMatchScore>) -> Unit = { onRefresh() },
) {
    val latestRefresh = rememberUpdatedState(onRefresh)
    val latestBroadcastRefresh = rememberUpdatedState(onBroadcastRefresh)
    val coroutineScope = rememberCoroutineScope()
    val sessionId = remember { generateSessionId() }
    val refreshKey = remember(leaseKey, watchPath) { leaseKey ?: watchPath.orEmpty() }
    var isVisible by remember { mutableStateOf(isDocumentVisible()) }
    var listenerVersion by remember { mutableStateOf(0) }
    var broadcastVersion by remember { mutableStateOf(0) }
    var changedScores by remember { mutableStateOf(emptyList<LiveMatchScore>()) }

    DisposableEffect(enabled, refreshKey, watchPath, sessionId) {
        val visibilityListener = { _: Event ->
            isVisible = isDocumentVisible()
            if (enabled && isVisible) {
                listenerVersion += 1
            }
        }
        document.addEventListener("visibilitychange", visibilityListener)

        val snapshotHandle = if (enabled) {
            attachFirestoreListener(coroutineScope, watchPath) { scores ->
                if (isVisible && scores.isNotEmpty()) {
                    changedScores = scores
                    broadcastVersion += 1
                }
            }
        } else null

        val broadcastHandle = attachBroadcastListener(refreshKey) { payload ->
            val sourceId = payload.sourceId as? String
            if (shouldIgnoreRefreshMessage(sourceId, sessionId)) return@attachBroadcastListener
            val key = payload.key as? String ?: return@attachBroadcastListener
            if (key == refreshKey && enabled && isVisible) {
                changedScores = emptyList()
                broadcastVersion += 1
            }
        }

        onDispose {
            document.removeEventListener("visibilitychange", visibilityListener)
            snapshotHandle?.invoke()
            broadcastHandle?.invoke()
        }
    }

    LaunchedEffect(enabled, refreshKey, intervalMs, listenerVersion) {
        if (!enabled) return@LaunchedEffect
        if (!isVisible) return@LaunchedEffect
        maybeRefresh(refreshKey, sessionId, intervalMs, latestRefresh)
        while (isActive) {
            delay(intervalMs)
            if (isVisible) {
                maybeRefresh(refreshKey, sessionId, intervalMs, latestRefresh)
            }
        }
    }

    LaunchedEffect(enabled, refreshKey, broadcastVersion) {
        if (!enabled || !isVisible || broadcastVersion == 0) return@LaunchedEffect
        latestBroadcastRefresh.value(changedScores)
    }
}

private suspend fun maybeRefresh(
    refreshKey: String,
    sessionId: String,
    intervalMs: Long,
    refresh: androidx.compose.runtime.State<suspend () -> Unit>,
) {
    if (!tryAcquireRefreshLease(refreshKey, sessionId, intervalMs)) return
    try {
        refresh.value()
        publishRefreshMessage(refreshKey, sessionId)
    } catch (e: Exception) {
        console.error("[LiveRefreshEffect] Erro no refresh: ${e.message}")
    }
}

private fun tryAcquireRefreshLease(
    refreshKey: String,
    sessionId: String,
    leaseMs: Long,
): Boolean {
    val currentLease = readRefreshLease(refreshKey)
    val nowMs = currentTimeMs()
    val request = RefreshLeaseRequest(
        ownerId = sessionId,
        nowMs = nowMs,
        leaseMs = leaseMs,
        visible = isDocumentVisible(),
    )
    if (!canAcquireRefreshLease(currentLease, request)) return false
    writeRefreshLease(refreshKey, RefreshLease(sessionId, nowMs))
    return true
}

private fun publishRefreshMessage(
    refreshKey: String,
    sessionId: String,
) {
    val channel = openBroadcastChannel(refreshChannelName(refreshKey)) ?: return
    try {
        val payload = js("{}")
        payload.type = "refresh-complete"
        payload.key = refreshKey
        payload.sourceId = sessionId
        payload.ts = currentTimeMs()
        channel.postMessage(payload)
    } catch (_: Exception) {
    } finally {
        try {
            channel.close()
        } catch (_: Exception) {
        }
    }
}

private fun attachBroadcastListener(
    refreshKey: String,
    onMessage: (dynamic) -> Unit,
): (() -> Unit)? {
    val channel = openBroadcastChannel(refreshChannelName(refreshKey)) ?: return null
    val handler = { event: dynamic ->
        try {
            onMessage(event.data)
        } catch (_: Exception) {
        }
    }

    try {
        channel.addEventListener("message", handler)
    } catch (_: Exception) {
        return null
    }

    return {
        try {
            channel.removeEventListener("message", handler)
            channel.close()
        } catch (_: Exception) {
        }
    }
}

private fun openBroadcastChannel(channelName: String): dynamic? {
    return try {
        js("typeof window !== 'undefined' && typeof window.BroadcastChannel !== 'undefined' ? new window.BroadcastChannel(channelName) : null")
    } catch (_: Throwable) {
        null
    }
}

private fun attachFirestoreListener(
    scope: CoroutineScope,
    watchPath: String?,
    onChange: (List<LiveMatchScore>) -> Unit,
): (() -> Unit)? {
    val path = watchPath?.takeIf { it.isNotBlank() } ?: return null
    val job: Job = scope.launch {
        awaitFirstPaint()
        try {
            FirestoreDatabase.instance()
            FirestoreDatabase.authenticate()
            var firstSnapshot = true
            FirestoreDatabase.instance().collection(path).snapshots.collect { snapshot ->
                if (firstSnapshot) {
                    firstSnapshot = false
                } else if (snapshot.documentChanges.isNotEmpty()) {
                    val scores = snapshot.documentChanges.mapNotNull { change ->
                        val document = change.document
                        if (!document.contains("live.score.home") || !document.contains("live.score.away")) return@mapNotNull null
                        val home = document.get<Int>("live.score.home")
                        val away = document.get<Int>("live.score.away")
                        if (home < 0 || away < 0) null else LiveMatchScore(document.id, home, away)
                    }.distinctBy { it.matchId }
                    onChange(scores)
                }
            }
        } catch (error: Exception) {
            console.error("[LiveRefreshEffect] Firestore listener failed: ${error.message}")
        }
    }
    return { job.cancel() }
}

private suspend fun awaitFirstPaint() {
    awaitRenderFrames { callback -> window.requestAnimationFrame { callback() } }
}

internal suspend fun awaitRenderFrames(requestFrame: (() -> Unit) -> Unit) {
    suspendCancellableCoroutine { continuation ->
        requestFrame {
            requestFrame {
                if (continuation.isActive) continuation.resume(Unit)
            }
        }
    }
}

private fun readRefreshLease(refreshKey: String): RefreshLease? {
    return try {
        val raw = window.localStorage.getItem(refreshLeaseStorageKey(refreshKey)) ?: return null
        val parts = raw.split("|", limit = 2)
        val ownerId = parts.getOrNull(0)?.takeIf { it.isNotBlank() } ?: return null
        val issuedAtMs = parts.getOrNull(1)?.toLongOrNull() ?: return null
        RefreshLease(ownerId, issuedAtMs)
    } catch (_: Exception) {
        null
    }
}

private fun writeRefreshLease(
    refreshKey: String,
    lease: RefreshLease,
) {
    try {
        val payload = "${lease.ownerId}|${lease.issuedAtMs}"
        window.localStorage.setItem(refreshLeaseStorageKey(refreshKey), payload)
    } catch (_: Exception) {
    }
}

private fun refreshLeaseStorageKey(refreshKey: String): String =
    "$LIVE_REFRESH_LEASE_PREFIX$refreshKey"

private fun refreshChannelName(refreshKey: String): String =
    "$LIVE_REFRESH_CHANNEL_PREFIX:$refreshKey"

private fun generateSessionId(): String =
    "tab-${currentTimeMs()}-${Random.nextInt(1_000_000)}"

private fun isDocumentVisible(): Boolean =
    (document.asDynamic().visibilityState as? String) == "visible"

private fun currentTimeMs(): Long =
    (js("Date.now()") as Number).toLong()
